import ZenProperties from "./ZenCore/ZenProperties.js";
import {copyBuffer} from "./CopyBuffer.js";
import {PropertyAccessor} from "./PropertyAccessor.js";


// change for gh

export const BlockChangeType = Object.freeze({
    sameItem:0,
    newItem:1,
    deletedItem:2
})

export class ChangedItem {
    constructor(blockChangeType, bytesChanged, lengthChanged, leftItem, rightItem) {
        this.blockChangeType = blockChangeType;
        this.bytesChanged = bytesChanged;
        this.lengthChanged = lengthChanged;
        this.leftItem = leftItem;
        this.rightItem = rightItem;
    }

    blockChangeType=0;
    bytesChanged=false;
    lengthChanged=false;

    leftItem=null;
    rightItem=null;
}

let displayModifiers = {
    plusOne: (value) => {
        return `${value} (${value + 1})`;
    },
  
};
/**
 *
 */
export class RolandStructuredObject {
    /*@type RolandStructuredFile*/
    file = null;
    /*@type DataView*/
    data = null;
    /**
     * start of this data in dataView
     * @type {null}
     */
    start = null;
    length = null;
    /**
     * How much of the total length is 'owned' by this enclosing object.
     * E.g. length of children - length.
     * @type {Number}
     */
    ownedLength = null;
    /*@type RolandStructuredObject*/
    parent = null;
    /*@type {RolandStructuredObject[]}*/
    children = [];
    description = undefined;
    childIndex = undefined;
    childIndexName = undefined;

    propertiesName = undefined;
    /**
     * New absolute position in new generated buffer
     * @type {*}
     */
    calculatedNewPosition = undefined;

    /**
     * Map of alternate name -> child index. Lives on every structured object
     * (not just arrays) so composite parents like MCPRJ can label their slots.
     */
    alternateNames = new Map();

    suppressCompare = false;
    suppressHex = false;

    /**
     *
     * @param file
     * @param data
     * @param parent
     * @param start
     * @param length
     * @param description used if nothing else available
     */
    constructor(file, data, parent, start, length, description) {
        this.file = file;
        this.data = data;
        this.start = start;
        this.ownedLength = this.length = length;
        this.parent = parent;
    }

    createChild(type, start, length, ...params) {
        return new type(this.file, this.data, this, start, length, ...params);
    }

    /**
     * Create, load and push child. returns New start address
     * @param lc
     * @param childType
     * @param start
     * @param params
     * @returns new start address
     */
    createAndLoadChild(lc, childType, start, ...params) {
        let child;
        child = new childType(this.file, this.data, this, start, ...params);

        child.nestedLoad(lc);
        this.children.push(child);
        return start + child.length;
    }

    /**
     * load item and children
     * @param lc loadContext
     */
    nestedLoad(lc) {
    }

    getString(offset, length) {
        let arr = new Uint8Array(this.data.buffer, this.start + offset, length);
        return String.fromCharCode.apply(String, arr);
    }

    getUint32(offset, le=true) {
        return this.data.getUint32(this.start + offset, le);
    }

    getUint16(offset, le=true) {
        return this.data.getUint16(this.start + offset, le);
    }

    getUint8(offset, le=true) {
        return this.data.getUint8(this.start + offset, le);
    }

    getInt32(offset, le=true) {
        return this.data.getInt32(this.start + offset, le);
    }

    getInt16(offset, le=true) {
        return this.data.getInt16(this.start + offset, le);
    }

    getInt8(offset, le=true) {
        return this.data.getInt8(this.start + offset, le);
    }

    setInt32(offset,value, le=true) {
        return this.data.setInt32(this.start + offset,value, le);
    }

    setInt16(offset,value, le=true) {
        return this.data.setInt16(this.start + offset,value, le);
    }

    setInt8(offset,value, le=true) {
        return this.data.setInt8(this.start + offset,value, le);
    }

    // Define the allowed characters for name string
    static allowedChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!#$%&'()+-.=@[]^_`{} ";
    // Create a regular expression from the allowed characters
    static allowedCharsRegex = new RegExp(`^[${this.allowedChars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}]*$`);

    /**
     * Sanitze textinput to allowable characters. Trims to 16 chars.
     * @param input
     * @returns {*}
     */
    static sanitizeText(input) {
        return input.split('').filter(char => RolandStructuredObject.allowedChars.includes(char)).join('').substring(0,16);
    }

    /**
     *
     * @param other
     * @param changeList
     */
    compareWith( other, changeList ) {
        if (this.suppressCompare)
            return;

        if (this.ownedLength!==other.ownedLength) {
            changeList.push( new ChangedItem(BlockChangeType.sameItem,false,true,this,other))
            return;
        }

        // Compare each byte
        let left = this.start;
        let right = other.start;
        for (let i = 0; i < this.ownedLength; i++) {
            if (this.data.getUint8(left++) !== other.data.getUint8(right++)) {
                changeList.push( new ChangedItem(BlockChangeType.sameItem,true,false,this,other))
                break;
            }
        }

        let maxChildrenLength = Math.max(this.children.length, other.children.length)
        for(let i=0;i<maxChildrenLength;i++ ) {
            if (i>=this.children.length) {
                changeList.push(new ChangedItem(BlockChangeType.deletedItem, false, false, undefined,other.children[i]))
                continue;
            }

            if (i>=other.children.length) {
                changeList.push(new ChangedItem(BlockChangeType.deletedItem, false, false,  this.children[i],undefined))
                continue;
            }

            this.children[i].compareWith(other.children[i],changeList);
        }
    }

    /**
     * Get the full name of the object, walking up through parents (separated by periods) adding in the
     * name if is a ZenNamedObject or child index if part of a RolandStructuredArrayBase
     * @returns {string} full name
     */
    getFullName() {
        let name = "";
        let obj = this;
        let child = undefined;
        while (obj) {
            let localName = obj.constructor.name;

            if (child && child.childIndex!==undefined)
                localName += "[" + (child.childIndexName ? child.childIndexName: child.childIndex) + "]";

            if (obj.getName!==undefined) {
                const n = obj.getName();
                if (n) localName += ' "'+n+'"';
            }

            if (obj.description)
                localName += ' '+obj.description;
            name = localName+(name.length>0 ? ("."+name) : "");

            child = obj;
            obj = obj.parent;
        }
        return name;

    }

    /**
     *
     * @param descriptions
     */
    setChildDescriptions(descriptions) {
        for(let i=0; i<descriptions.length && i<this.children.length;i++)
            this.children[i].description = descriptions[i];
    }

    /**
     * Go down the objects calculating the length
     * calculatedNewPosition is the new abosolute position in the new buffer. Only really useful for chunks as only thing
     * with an absolute position.
     */
    calculateLength(destination) {
        this.calculatedNewPosition = destination;
        destination+=this.ownedLength

        let length = this.ownedLength;
        this.children.forEach( (child) => {
            let childLength = child.calculateLength(destination);
            length += childLength;
            destination += childLength;
        });
        this.length = length;
        return length;
    }

    /**
     * nested function to copy this object's owned length, then all its children to a new DataView
     * @param destination
     * @param dv
     * @return new destination, i.e. after this object has been written
     */
    writeDataToView( destination, dv) {
        destination = copyBuffer(destination, dv, this.start, this.data, this.ownedLength);

        this.children.forEach( (child) => {
            destination = child.writeDataToView( destination, dv );
        })
        return destination;
    }

    /**
     * Overwrite the data of this object with the data of another object
     * @param {RolandStructuredObject} source 
     */
    overwriteDataSameSize( source ) {
        if (this.ownedLength!==source.ownedLength || this.length!==source.length)
            throw `Cannot overwrite data of ${this.getFullName()} with ${source.getFullName()} because they have different lengths`;

        copyBuffer(this.start, this.data, source.start, source.data, this.length);
    }


    /**
     * Build a breadcrumb-style accessor for this object's descriptor. Pass a
     * path like "PCMT_PTL[2].PTL_FENV" to descend further. Call `getUint32`,
     * `setInt16`, etc. on the returned accessor to read/write named leaf fields.
     * @param {string} [path]
     * @returns {PropertyAccessor}
     */
    at(path) {
        return PropertyAccessor.fromObject(this, path);
    }

    /**
     * Get the names of the children (assuming they expose getName).
     */
    getNames() {
        return this.children.map(c => c.getName?.());
    }

    /** First child whose getName matches; undefined otherwise. */
    findName(name) {
        return this.children.find(c => c.getName?.() === name);
    }

    /** First child index whose getName matches; undefined otherwise. */
    findNameIndex(name) {
        const i = this.children.findIndex(c => c.getName?.() === name);
        return i < 0 ? undefined : i;
    }

    /** Indexes of children whose names do NOT match. */
    findNotName(name) {
        const r = [];
        this.children.forEach((c, i) => { if (c.getName?.() !== name) r.push(i); });
        return r;
    }

    /** Indexes of children whose names DO match. */
    findAllOfName(name) {
        const r = [];
        this.children.forEach((c, i) => { if (c.getName?.() === name) r.push(i); });
        return r;
    }

    /**
     * Register a map of alternate-name -> child index for this object.
     * @param {Object} object - keys are alternate names, values are child indexes
     */
    setAlternateNames(object) {
        for (const [name, index] of Object.entries(object))
            this.alternateNames.set(name, index);
    }

    /** Resolve a child by registered alternate name. */
    findChildByAlternateName(name) {
        return this.children[this.alternateNames.get(name)];
    }

    /** Resolve a child index by registered alternate name. */
    findChildIndexByAlternateName(name) {
        return this.alternateNames.get(name);
    }

    /**
     * Reverse-lookup: given an index or child object, find the alternate name.
     */
    findAlternateNameForChild(indexOrObject) {
        if (typeof indexOrObject === 'number') {
            for (const [k, v] of this.alternateNames)
                if (v === indexOrObject) return k;
        } else if (indexOrObject instanceof RolandStructuredObject) {
            for (const [k, v] of this.alternateNames)
                if (this.children[v] === indexOrObject) return k;
        }
        return undefined;
    }

    /** All registered alternate names. */
    getAlternateNames() {
        return [...this.alternateNames.keys()];
    }

    /**
     * Convert a 0 based number to a Roland patch string (XX-YY)
     * @param {*} num 
     * @param {*} maxPatch 
     * @returns {string}     XX-YY
     */
    static toRolandPatch(num, maxPatch = 8) {
        const bank = Math.floor(num / maxPatch) + 1;  // Bank number (1-based)
        const patch = (num % maxPatch) + 1;           // Patch number (1-based)
      
        // Format with leading zeros to "XX-YY"
        const bankStr = String(bank).padStart(2, "0");
        const patchStr = String(patch).padStart(2, "0");
        return `${bankStr}-${patchStr}`;
      }

      /**
       * Add a display modifier
       * @param {string} modifierName
       * @param {function} modifierFunction
       */
      static addDisplayModifier(modifierName, modifierFunction) {
        displayModifiers[modifierName] = modifierFunction;
      }

    /**
     * Build a thin RolandStructuredObject subclass driven entirely by a
     * ZenProperties descriptor. The class's instances have:
     *   - `propertiesName` set to `propertiesName`
     *   - length fixed to the descriptor's `byteLength`
     *   - `getName()`/`setName()` resolved from a NAME_1 leaf inside the
     *     descriptor (searched recursively through composite sub-blocks);
     *     returns undefined / throws when no NAME_1 exists.
     *
     * Extra constructor args (e.g. childArrayTypes for arrays) are ignored.
     *
     * @param {string} propertiesName key into ZenProperties
     * @returns {typeof RolandStructuredObject}
     */
    static fromDescriptor(propertiesName) {
        const desc = ZenProperties[propertiesName];
        if (!desc)
            throw new Error(`No ZenProperties entry for "${propertiesName}"`);
        const fixedLength = desc.byteLength ?? RolandStructuredObject._descriptorByteLength(desc);

        return class DescriptorBacked extends RolandStructuredObject {
            constructor(file, data, parent, start /*, length, ... */) {
                super(file, data, parent, start, fixedLength);
                this.propertiesName = propertiesName;
            }
        };
    }

    /**
     * Compute a descriptor's byteLength as the max(byteOffset + byteLength)
     * over its parameters. Used when the descriptor itself has no top-level
     * byteLength (composite entries in jupiter.js).
     */
    static _descriptorByteLength(descriptor) {
        let max = 0;
        for (const p of Object.values(descriptor.parameters || {})) {
            const end = (p.byteOffset || 0) + (p.byteLength || 0);
            if (end > max) max = end;
        }
        return max;
    }

    /**
     * Walk a descriptor recursively to find the first NAME_1 leaf. Returns
     * the offset relative to `baseOffset`, or undefined if not found.
     */
    static _findNameOffset(descriptor, baseOffset) {
        const params = descriptor?.parameters;
        if (!params) return undefined;
        const direct = params.NAME_1;
        if (direct && !direct.block)
            return baseOffset + direct.byteOffset;
        for (const p of Object.values(params)) {
            if (p.block) {
                const found = RolandStructuredObject._findNameOffset(p.block, baseOffset + p.byteOffset);
                if (found !== undefined) return found;
            }
        }
        return undefined;
    }
}

/**
 *
 */
export class RolandStructuredNamedObject extends RolandStructuredObject {
    stringAddress=0;

    constructor(file, data, parent, start, length,stringAddress=0) {
        super(file, data, parent, start, length);
        this.stringAddress=stringAddress;
    }

    getName() {
        return this.getString(this.stringAddress, 16).trimEnd();
    }

    setName(value) {
        value = value.replace(/[^\x20-\x7F]/g, "");
        value = value.substring(0,16);
        value = value.padEnd(16," ");

        for(let i=0;i<16;i++) {
            const c = value.codePointAt(i) || ' ';
            this.setInt8(this.stringAddress+i,c);
        }
    }

    /**
     * Build a RolandStructuredNamedObject subclass driven by a ZenProperties
     * descriptor, with an explicit name offset override.
     *
     * If `nameStartOffset` is omitted, the first `NAME_1` leaf is searched for
     * recursively through composite sub-blocks and used as the 16-byte name.
     *
     * @param {string} propertiesName key into ZenProperties
     * @param {number|undefined|null} [nameStartOffset] absolute offset (from object start) of 16-byte name
     * @returns {typeof RolandStructuredNamedObject}
     */
    static fromDescriptor(propertiesName, nameStartOffset = undefined) {
        const desc = ZenProperties[propertiesName];
        if (!desc)
            throw new Error(`No ZenProperties entry for "${propertiesName}"`);
        const fixedLength = desc.byteLength ?? RolandStructuredObject._descriptorByteLength(desc);

        const resolvedNameOffset = (nameStartOffset === undefined || nameStartOffset === null)
            ? RolandStructuredObject._findNameOffset(desc, 0)
            : nameStartOffset;

        return class NamedDescriptorBacked extends RolandStructuredNamedObject {
            constructor(file, data, parent, start /*, length, ... */) {
                super(file, data, parent, start, fixedLength, resolvedNameOffset ?? 0);
                this.propertiesName = propertiesName;
            }
            getName() {
                if (resolvedNameOffset === undefined) return undefined;
                return this.getString(resolvedNameOffset, 16).replace(/\s+$/, '');
            }
            setName(value) {
                if (resolvedNameOffset === undefined)
                    throw new Error(`Descriptor "${propertiesName}" has no NAME_1`);
                value = (value ?? '').replace(/[^\x20-\x7F]/g, '')
                    .substring(0, 16).padEnd(16, ' ');
                for (let i = 0; i < 16; i++)
                    this.setInt8(resolvedNameOffset + i, value.codePointAt(i) ?? 0x20);
            }
        };
    }
}
